import { EventStatus, Role, Event, User, EventType, StaffType } from '../services/Types';

export const usePermissions = (eventData: Event) => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  const canUserApproveEvent = (() => {
    if (user.approverLevel === 1 && eventData.status === EventStatus.APPROVAL1) return true;
    if (user.approverLevel === 2 && eventData.status === EventStatus.APPROVAL2) return true;
    if (user.approverLevel === 3 && eventData.status === EventStatus.APPROVAL3) return true;
    return false;
  })();

  const userIsLoggedIn = user.id != null;
  const userIsOwner = userIsLoggedIn && (eventData.createrId === user.id || eventData.creater?.id === user.id);
  const userIsAdmin = user.role === Role.ADMIN;
  const userIsRequester = user.role === Role.REQUESTER
    || (user.id && eventData.createrId && eventData.createrId === user.id)
    || (user.id && eventData.creater?.id === user.id);
  const userIsApprover = user.role === Role.APPROVER;

  const canUserDelete = userIsAdmin;
  const canUserRevert = userIsAdmin;
  const canUserCancel = userIsAdmin || userIsOwner;
  const canUserViewEvent = userIsAdmin || userIsOwner;
  const canUserViewCloseout = user.role === Role.ADMIN;
  const eventIsWebinar = eventData.eventType === EventType.WEBINAR;
  const eventIsSeries = eventData.eventType === EventType.SERIES;
  const eventIsPodcast = eventData.eventType === EventType.PODCAST;
  const eventIsWebcast = eventData.eventType === EventType.WEBCAST;
  const eventIsSaved = eventData.status === EventStatus.SAVED;
  const eventIsRejected = eventData.status === EventStatus.REJECTED;
  const eventIsApproved = eventData.status === EventStatus.APPROVED;
  const eventIsCancelled = eventData.status === EventStatus.CANCELLED;
  const eventIsBlocked = eventData.status === EventStatus.BLOCKED || eventData.eventType === EventType.BLOCKED;
  const eventBasicInfoIsComplete = Boolean((eventData.programOfficeId || 0) > 0 && (eventData.levelOneApproverId || 0) > 0);
  const eventInfoIsComplete = Boolean(eventData.title && eventData.summary);
  const eventStaffIsComplete = Boolean(eventData.tbd_moderators || eventData.staff?.some(s => s.staff_type === StaffType.MODERATORS))
    && Boolean(eventData.tbd_presenters || eventData.staff?.some(s => s.staff_type === StaffType.PRESENTERS));
  const eventAudienceIsComplete = true;
  const eventFeaturesAndRequestsIsComplete = true;
  const eventBookingIsComplete = (
    (!eventIsSeries && !eventIsBlocked) ? (eventData.roomBooking?.length > 0
      && eventData.roomBooking?.some(r => r.category === 'live')) : true)
    && ((eventIsPodcast) ? Boolean(eventData.publicationDate) : true)
    && ((eventIsWebcast) ? Boolean(eventData.publicationDate) : true);
  const userCanSubmit = userIsRequester && !eventIsBlocked && !eventIsCancelled
    && !eventIsRejected && !eventIsApproved
    && Boolean(eventData.programOfficeId) && eventData.programOfficeId! > 0
    && Boolean(eventData.levelOneApproverId) && eventData.levelOneApproverId! > 0;
  const eventIsUnlocked = (userIsRequester && eventData.status === EventStatus.SAVED)
    || (userIsApprover && (eventData.status === EventStatus.APPROVAL1
      || eventData.status === EventStatus.APPROVAL2
      || eventData.status === EventStatus.APPROVAL3))
    || userIsAdmin;
  const eventIsReady = eventBasicInfoIsComplete
    && eventInfoIsComplete
    && eventStaffIsComplete
    && eventAudienceIsComplete
    && eventBookingIsComplete;
  const eventReadyToApprove = Boolean(canUserApproveEvent
    && !eventIsBlocked
    && eventIsReady
  );

  // Check if event is in any approval state
  const eventIsInApprovalState = eventData.status === EventStatus.APPROVAL1
    || eventData.status === EventStatus.APPROVAL2
    || eventData.status === EventStatus.APPROVAL3;

  // Button visibility variables
  // Requester should not see Unlock button when event is in any APPROVAL state
  // Note: eventIsApproved already implies !eventIsInApprovalState, so no need to check both
  const showButtonUnlock = userIsRequester && eventIsApproved;
  const showButtonSave = userIsAdmin
    || (userIsRequester && eventIsSaved)
    || canUserApproveEvent;
  const showButtonApprove = userIsApprover && canUserApproveEvent && !eventIsBlocked;
  const showButtonReject = userIsApprover && canUserApproveEvent && !eventIsBlocked;
  const showButtonBack = userIsRequester && eventIsSaved;
  const showButtonSaveAndSubmit = userIsRequester && eventIsSaved;
  const showButtonViewSeriesParent = Boolean(eventData.seriesId) && eventData.eventType !== EventType.SERIES;
  // Requester should not see Cancel button when event is in any APPROVAL state
  // Admin can always cancel (when not cancelled/blocked)
  // Requester (who is owner) can cancel when NOT in approval state (i.e., when approved or saved)
  const showButtonCancel = !eventIsCancelled && !eventIsBlocked && (
    userIsAdmin 
    || (userIsOwner && !eventIsInApprovalState)
  );
  const showButtonRevert = canUserRevert && !eventIsBlocked;
  // Admins can transfer an event at any point; the requester loses that ability
  // once the event is with an approver.
  const showButtonTransferRequester = !eventIsCancelled && !eventIsBlocked && (
    userIsAdmin
    || (userIsOwner && !eventIsInApprovalState)
  );
  // Allow admins to delete any event, or owners to delete their own BLOCKED events
  const showButtonDelete = canUserDelete || (userIsOwner && eventIsBlocked);
  const disableButtonSaveAndSubmit = !(userCanSubmit && eventIsReady);

  return {
    canUserApproveEvent,
    canUserDelete,
    canUserRevert,
    canUserCancel,
    canUserViewEvent,
    canUserViewCloseout,
    userIsAdmin,
    userIsApprover,
    userIsRequester,
    userCanSubmit,
    eventIsBlocked,
    eventIsSaved,
    eventIsRejected,
    eventIsApproved,
    eventIsCancelled,
    eventIsUnlocked,
    eventReadyToApprove,
    eventStaffIsComplete,
    eventBookingIsComplete,
    eventInfoIsComplete,
    eventBasicInfoIsComplete,
    eventAudienceIsComplete,
    eventFeaturesAndRequestsIsComplete,
    eventIsWebinar,
    eventIsSeries,
    eventIsPodcast,
    eventIsWebcast,
    // Button visibility variables
    showButtonUnlock,
    showButtonSave,
    showButtonApprove,
    showButtonReject,
    showButtonBack,
    showButtonSaveAndSubmit,
    showButtonViewSeriesParent,
    showButtonCancel,
    showButtonRevert,
    showButtonDelete,
    showButtonTransferRequester,
    disableButtonSaveAndSubmit,
    // Add more as you add rules
  };
}; 